import clsx from 'clsx';
import { MouseEvent, forwardRef } from 'react';
import { twMerge } from 'tailwind-merge';
import Icon from '../icon/icon';
import Input, { InputProps } from '../input/input';
import Text from '../text/text';

/* eslint-disable-next-line */
export type BG_BUTTON_TYPES = 'primary' | 'base' | 'baseError' | 'transparent' | 'error';

const BG_BUTTON_CLASSES = {
    primary: 'bg-primary active:bg-primary-50 active:text-primary disabled:bg-primary-50 disabled:text-primary group',
    primaryLight:
        'bg-[color:var(--variant-50)] active:bg-[color:var(--variant-50)] !text-[color:var(--variant-800)] active:text-[color:var(--variant-800)]  disabled:!text-white group',
    base: 'bg-gray-50 group-focus-within:bg-primary-50 group',
    transparent: 'bg-transparent group',
    error: 'bg-secondary active:bg-secondary-50 active:text-secondary disabled:bg-secondary-50 disabled:text-secondary group',
    baseError: 'bg-gray-50 group-focus-within:bg-secondary-50 disabled:bg-gray-50 focus:!bg-gray-50 group',
};

const ICON_BASE_CLASSES = {
    primary: 'text-white group-one-active:text-primary group-one-disabled:text-primary',
    base: 'text-gray-900 group-focus-within:text-primary',
    transparent: 'text-stone-500',
    error: 'text-white group-one-active:text-secondary group-one-disabled:text-secondary',
    baseError: 'text-gray-900 group-focus-within:text-secondary',
};

const COMMON_BUTTON_CLASSES = 'absolute top-[1px] bottom-[1px] flex justify-center items-center';

export interface InputButton {
    iconName?: string;
    background: BG_BUTTON_TYPES;
    iconClass?: string;
    buttonClass?: string;
}

export interface ActionLabeledInputButton extends InputButton {
    position?: 'left' | 'right';
    label?: string;
    action?(event: MouseEvent): void;
}
export interface TextFieldProps extends InputProps {
    label?: string;
    helper?: string;
    errorMessage?: string;
    startButton?: InputButton;
    endButton?: ActionLabeledInputButton;
    formik?: any;
    readonly?: boolean;
    required?: boolean;
    buttonDisabled?: boolean;
    classNameContainer?: string;
    autocompleteOptions?: string[];
    handleAutocomplete?(option: string): void;
    dataTestId?: string;
    tooltip?: string;
    type?: string;
    id?: string;
}

export const TextField = forwardRef<HTMLInputElement, TextFieldProps>((props, ref) => {
    const endIcon = props?.endButton?.label ? (
        <>
            <p className={props.endButton?.position === 'left' ? 'pr-sm' : 'pl-sm'}>{props?.endButton?.label}</p>
            {props?.endButton?.iconName && (
                <Icon
                    className={twMerge(
                        ICON_BASE_CLASSES[
                            props.error
                                ? props.endButton?.background === 'primary'
                                    ? 'error'
                                    : 'baseError'
                                : props?.endButton?.background || 'base'
                        ],
                        props.endButton?.iconClass,
                    )}
                    name={props?.endButton?.iconName}
                    size={14}
                />
            )}
            {props?.endButton?.iconName && props?.tooltip && (
                <Text
                    className="absolute top-6 left-4 z-10 hidden group-hover:block bg-slate-700 rounded p-1"
                    color="white"
                >
                    {props?.tooltip}
                </Text>
            )}
        </>
    ) : (
        <>
            <Icon
                className={twMerge(
                    ICON_BASE_CLASSES[
                        props.error
                            ? props.endButton?.background === 'primary'
                                ? 'error'
                                : 'baseError'
                            : props?.endButton?.background || 'base'
                    ],
                    props.endButton?.iconClass,
                )}
                name={props?.endButton?.iconName}
                size={14}
            />
            {props?.tooltip && (
                <Text
                    className="absolute top-6 left-4 z-10 hidden group-hover:block bg-slate-700 rounded p-1"
                    color="white"
                >
                    {props?.tooltip}
                </Text>
            )}
        </>
    );

    const startButtonClasses = clsx(
        COMMON_BUTTON_CLASSES,
        props?.startButton?.buttonClass,
        BG_BUTTON_CLASSES[props.error ? 'baseError' : props.startButton?.background || 'base'],
        'left-[1px] px-3 rounded-l',
    );

    const endButtonClasses = clsx(
        'group-one',
        COMMON_BUTTON_CLASSES,
        props?.endButton?.buttonClass,
        BG_BUTTON_CLASSES[
            props.error
                ? props.endButton?.background === 'primary'
                    ? 'error'
                    : 'baseError'
                : props?.endButton?.background || 'base'
        ],
        props?.endButton?.label ? 'px-4 text-white text-sm font-semibold font-poppins' : 'px-3',
        props.endButton?.position === 'left' ? 'flex-row' : 'flex-row-reverse',
        'right-[1px] rounded-r',
    );

    const inputCustomClasses = twMerge(
        props?.startButton?.iconName ? 'pl-11' : 'pl-sm',
        props.formik &&
            props.formik.touched[props.name] &&
            props.formik.errors[props.name] &&
            '!border !ring-0 !border-red-500',
        props.className,
    );

    return (
        <div className={twMerge('flex flex-col-reverse', props.classNameContainer)}>
            {props.errorMessage && <p className="text-xs text-error-200 mt-1 font-normal">{props.errorMessage}</p>}
            {props.helper && <p className="text-xs text-gray-300 mt-1 font-normal">{props.helper}</p>}
            <div
                className="relative group"
                data-test-id={props.dataTestId || `text-field-${props.name}`}
                data-testid={props.dataTestId || `text-field-${props.name}`}
            >
                {props?.startButton?.iconName && (
                    <button className={startButtonClasses} type="button" disabled={props.buttonDisabled}>
                        <Icon
                            className={twMerge(
                                ICON_BASE_CLASSES[props.error ? 'baseError' : props?.startButton?.background || 'base'],
                                props.startButton?.iconClass,
                            )}
                            name={props?.startButton?.iconName}
                            size={14}
                        />
                    </button>
                )}
                <Input
                    ref={ref}
                    id={props.id}
                    name={props.name}
                    value={props.value}
                    defaultValue={props.defaultValue}
                    rounded={props.rounded}
                    error={props.error}
                    placeholder={props.placeholder}
                    disabled={props.disabled}
                    onClick={props.onClick}
                    onBlur={props.onBlur}
                    onChange={props.onChange}
                    onKeyDown={props.onKeyDown}
                    onFocus={props.onFocus}
                    className={inputCustomClasses}
                    size={props.size}
                    style={props.style}
                    type={props.type || 'text'}
                    readonly={props.readonly}
                    autoFocus={props.autoFocus}
                    checked={props.checked}
                />
                {props.endButton && (
                    <button
                        className={endButtonClasses}
                        type="button"
                        disabled={props.buttonDisabled}
                        onClick={(evt) => {
                            props.endButton?.action
                                ? props.endButton?.action(evt)
                                : props.onClick && props.onClick(evt);
                        }}
                    >
                        {endIcon}
                    </button>
                )}
                {props.autocompleteOptions && props.autocompleteOptions.length > 0 && (
                    <div className="w-full absolute flex flex-col bg-white rounded z-20 shadow-[0px_3px_10px_0px_rgba(20,115,247,0.12)]">
                        {props.autocompleteOptions.map((option) => (
                            <div onClick={() => props.handleAutocomplete && props.handleAutocomplete(option)}>
                                <Text className="cursor-pointer hover:bg-variant-50 p-2" lineHeight="16">
                                    {option}
                                </Text>
                            </div>
                        ))}
                    </div>
                )}
            </div>
            {props.label && (
                <div className="flex">
                    <label className="mb-1 text-sm font-medium text-black flex" htmlFor={props.id}>
                        {props.label}
                    </label>
                    {props.required && <span className="text-red-600">&nbsp; * </span>}
                </div>
            )}
        </div>
    );
});

TextField.defaultProps = {
    label: '',
};

export default TextField;
